<template>
    <div
        class="group my-4 rounded-lg border-iron-200 bg-white px-4 py-5 shadow-sm hover:shadow-md w-full border transition-all duration-200"
    >
        <div class="space-x-3 sm:flex items-center justify-between">
            <div class="gap-2 flex items-center">
                <div class="flex-shrink-0"></div>
                <div class="min-w-0 flex-1">
                    <p class="font-medium text-iron-900">
                        {{ dayOff.name }}
                    </p>

                    <p
                        v-if="
                            dayOff.end &&
                            isSameDay(
                                new Date(dayOff.start),
                                new Date(dayOff.end),
                            )
                        "
                        class="text-sm text-iron-700"
                    >
                        {{ format(new Date(dayOff.start), "PPpp") }} -
                        {{ format(new Date(dayOff.end), "pp") }}
                    </p>
                    <p v-else-if="dayOff.end" class="text-sm text-iron-700">
                        {{ format(new Date(dayOff.start), "PPpp") }} -
                        {{ format(new Date(dayOff.end), "PPpp") }}
                    </p>
                    <p v-else class="text-sm text-iron-700">
                        {{ format(new Date(dayOff.start), "PPpp") }}
                    </p>
                </div>
            </div>

            <div
                class="mt-3 sm:mt-0 flex flex-shrink-0 justify-start self-center"
            >
                <div class="rounded-md shadow-sm inline-flex">
                    <ManageDayOff
                        :day-off-end="dayOff.end"
                        :day-off-start="dayOff.start"
                        :day-off-name="dayOff.name"
                        :day-off-id="dayOff.id"
                    >
                        <button
                            class="gap-2 rounded-l-md rounded-r-md bg-white px-3 py-2 text-sm font-medium text-iron-900 ring-iron-300 hover:bg-iron-50 relative -ml-px inline-flex items-center ring-1 ring-inset focus:z-10"
                        >
                            <Edit2 :size="20" :stroke-width="1.5" />
                            <span class="sm:inline-block hidden">{{
                                $t("edit")
                            }}</span>
                        </button>
                    </ManageDayOff>

                    <button
                        class="ml-2 gap-2 rounded-l-md rounded-r-md bg-white px-3 py-2 text-sm font-medium text-iron-900 ring-iron-300 hover:bg-iron-50 relative inline-flex items-center ring-1 ring-inset focus:z-10"
                        @click="removeDayOff"
                    >
                        <Trash2 :size="20" :stroke-width="1.5" />
                        <span class="sm:inline-block hidden">{{
                            $t("remove")
                        }}</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import ManageDayOff from "./ManageDayOff.vue";
import useConfirm from "../../ConfirmDialog/composables/useConfirm";
import { useI18n } from "vue-i18n";
import { useLocalisedTzFormat } from "@/composables/useLocalisedTzFormat";
import { Trash2, Edit2 } from "lucide-vue-next";
import { isSameDay } from "date-fns";
import type { DayOffDTO } from "@/types/DaysOff";
import { useDaysOff } from "@/composables/useDaysOff";

const { show } = useConfirm();
const { t } = useI18n();
const { format } = useLocalisedTzFormat();
const { deleteDayOff } = useDaysOff();

const props = defineProps<{
    dayOff: DayOffDTO;
}>();

const removeDayOff = async () => {
    const confirmed = await show({
        header: t("days_off.are_you_sure"),
        message: t("days_off.delete"),
        confirm: t("days_off.remove"),
        cancel: t("cancel"),
    });

    if (!confirmed) {
        return;
    }

    deleteDayOff(props.dayOff.id);
};
</script>
